import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useCallback, useEffect, useRef, useState } from 'react';

import { haptic } from '../haptic.js';
import { relativeSprintTitle } from '../sprint/labels.js';
import { RowAction } from '../task/RowActions.js';
import {
  ClosingTimer,
  DeleteHoldTimer,
  DoneToggle,
  TaskMarks,
  openOnBlankClick,
} from '../task/TaskParts.js';
import { shownStatus, useClosingHold } from '../task/closing.js';
import { pointerListeners } from '../task/drag.js';
import { useLanded } from '../task/landed.js';

import { groupByStatus } from './grouping.js';
import { planListDrop } from './order.js';
import { SETTLE_MS, swipeTargets, useSwipe } from './swipe.js';

import type { ClosingHold } from '../task/closing.js';
import type { Placement } from '../task/landed.js';
import type { SwipeTarget, SwipeTargets, SwipeView } from './swipe.js';
import type { TaskExtras, TaskHandlers } from '../task/TaskParts.js';
import type { DragEndEvent, DragMoveEvent, DragStartEvent, Modifier } from '@dnd-kit/core';
import type { SprintKey, Task } from '@nedelnik/shared';

/**
 * Сколько держать палец на строке, чтобы её взять (3.1.11). Полсекунды — как
 * долгое нажатие в самой iOS; две секунды ощущались бы как «не сработало».
 * Решение владельца 2026-10-11.
 */
const LONG_PRESS_MS = 500;

/**
 * Сколько держать строку неподвижно, чтобы на ней появилась кнопка
 * «Удалить» (3.1.14): полсекунды до подъёма и ещё полторы на месте. Предложение
 * владельца 2026-10-24 — удалять, не открывая карточки, и не только во
 * Входящих. Долго нарочно: короче — и удаление попадалось бы под руку при
 * каждой задумчивой перестановке.
 */
export const DELETE_HOLD_MS = 2000;

/**
 * Насколько можно сдвинуть поднятую строку, чтобы это ещё было «держу», а не
 * «несу»: палец на стекле не стоит идеально ровно.
 */
const HOLD_STILL_PX = 10;

/**
 * Сколько после броска или свайпа гасить клик. Отпущенный после долгого
 * нажатия или жеста палец iOS превращает в клик по строке, и вместо
 * перестановки открывалась бы карточка задачи.
 */
const CLICK_AFTER_DROP_MS = 400;

/**
 * Строку несут только вверх и вниз. Вбок ей ехать некуда, а уехав за палец в
 * сторону, она упиралась в край экрана — выглядело, будто строка заходит за
 * невидимую стенку (замечание владельца 2026-10-18).
 */
const verticalOnly: Modifier = ({ transform }) => ({ ...transform, x: 0 });

/**
 * Список — плоский перечень, сгруппированный по статусам (3.1). Те же задачи,
 * что и на доске, и те же элементы управления: формат меняет расположение, а не
 * набор действий.
 *
 * Строку можно взять долгим нажатием и переставить (3.1.11), в том числе в
 * другую группу — тогда меняется и статус. Без `onDrop` список неподвижен: так
 * он стоит на экране проекта, где задачи из разных недель и вложенность.
 * Свайпом вбок задача переезжает между неделей и Входящими (3.1.12).
 */
export function TaskList({
  tasks,
  extras,
  handlers,
  onDrop,
}: {
  tasks: readonly Task[];
  extras: TaskExtras;
  handlers: TaskHandlers;
  onDrop?: (task: Task, placement: Placement) => void;
}): React.ReactElement {
  // Закрытая задача пару секунд остаётся на месте (3.1.8): иначе случайное
  // нажатие проходит незамеченным — задача просто пропадает из виду.
  const closing = useClosingHold(tasks, { close: handlers.onClose, reopen: handlers.onReopen });
  const { shown, land } = useLanded(tasks);
  const groups = groupByStatus(shown, closing.statusOf);
  const [dragging, setDragging] = useState<string | undefined>(undefined);
  const quietSince = useRef(0);
  const quietClicks = useCallback(() => {
    quietSince.current = Date.now();
  }, []);

  // Удаление удержанием (3.1.14): `holding` — строку подняли и держат на
  // месте, по ней идёт полоса; `armed` — додержали, на строке кнопка
  // «Удалить», пока не нажмут её или куда-нибудь ещё.
  const [holding, setHolding] = useState<string | undefined>(undefined);
  const [armed, setArmed] = useState<string | undefined>(undefined);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const stopHold = useCallback(() => {
    clearTimeout(holdTimer.current);
    holdTimer.current = undefined;
    setHolding(undefined);
  }, []);
  useEffect(() => () => clearTimeout(holdTimer.current), []);

  // Кнопка снимается касанием мимо неё — как меню. Слушатель на документе в
  // фазе захвата: касание по другой строке иначе успело бы её поднять.
  useEffect(() => {
    if (armed === undefined) return undefined;
    function onDown(event: PointerEvent): void {
      if (event.target instanceof Element && event.target.closest('.row__delete') !== null) return;
      setArmed(undefined);
    }
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [armed]);

  const sensors = useSensors(
    // Мышь — только на узком окне компьютера: телефону хватает касания, а
    // датчик указателя на телефоне перехватывал бы прокрутку.
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    // Сдвиг пальца до срабатывания — это прокрутка, а не захват.
    useSensor(TouchSensor, { activationConstraint: { delay: LONG_PRESS_MS, tolerance: 8 } }),
  );

  function handleDragStart(event: DragStartEvent): void {
    const id = String(event.active.id);
    setDragging(id);
    setArmed(undefined);
    // Удалять можно только там, где строку вообще берут: в проекте список
    // неподвижен, и удержание там ничего не начинает.
    setHolding(id);
    clearTimeout(holdTimer.current);
    holdTimer.current = setTimeout(() => {
      holdTimer.current = undefined;
      setHolding(undefined);
      setArmed(id);
      // На iPhone не прозвучит (таймер вне касания) — там знак — сама кнопка
      // и полоса до неё.
      haptic('strong');
    }, DELETE_HOLD_MS - LONG_PRESS_MS);
    // Отклик — знак, что строка в руке, двойной. На iPhone его не будет:
    // захват случается по таймеру, а iOS щёлкает только в момент касания или
    // отпускания (проверено владельцем 2026-10-18). Там знак захвата —
    // подъём строки (`.row.is-dragging`), а щелчок звучит при броске.
    haptic('strong');
  }

  function handleDragMove({ delta }: DragMoveEvent): void {
    if (Math.hypot(delta.x, delta.y) <= HOLD_STILL_PX) return;
    // Понесли — значит переставляют, а не удаляют: ни полосы, ни кнопки.
    if (holdTimer.current !== undefined) stopHold();
    if (armed !== undefined) setArmed(undefined);
  }

  function handleDragEnd({ active, over }: DragEndEvent): void {
    setDragging(undefined);
    stopHold();
    quietClicks();
    // Бросок — отпускание пальца, и здесь iOS щелчок пропускает:
    // `@dnd-kit` зовёт этот обработчик прямо из `touchend`.
    haptic();
    if (onDrop === undefined || over === null) return;

    const placement = planListDrop(groups, String(active.id), String(over.id));
    const task = tasks.find((item) => item.id === active.id);
    if (placement === undefined || task === undefined) return;
    if (task.status === placement.status && task.order_key === placement.order_key) return;

    land(task.id, placement);
    onDrop(task, placement);
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[verticalOnly]}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragCancel={() => {
        setDragging(undefined);
        stopHold();
        setArmed(undefined);
      }}
      onDragEnd={handleDragEnd}
    >
      {/*
        Один `SortableContext` на весь список, а не на группу: строки
        расступаются и через заголовок соседней группы, и задачу видно там,
        куда она встанет, — в том числе в другом статусе.
      */}
      <SortableContext
        items={groups.flatMap((group) => group.tasks.map((task) => task.id))}
        strategy={verticalListSortingStrategy}
      >
        <div
          className={`list${dragging === undefined ? '' : ' is-sorting'}`}
          onClickCapture={(event) => {
            if (Date.now() - quietSince.current < CLICK_AFTER_DROP_MS) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
        >
          {groups.map((group) => (
            <section key={group.status} className="list__group">
              <h2 className="list__title">
                <i className={`dot dot--${group.status}`} aria-hidden="true" />
                {group.title}
                <span className="count">{group.tasks.length}</span>
              </h2>
              <ul className="list__rows">
                {group.tasks.map((task) => (
                  <Row
                    key={task.id}
                    task={task}
                    extras={extras}
                    handlers={handlers}
                    closing={closing}
                    sortable={onDrop !== undefined}
                    swipeEnabled={dragging === undefined && armed === undefined}
                    quietClicks={quietClicks}
                    hold={holding === task.id ? 'holding' : armed === task.id ? 'armed' : undefined}
                    onDelete={() => {
                      setArmed(undefined);
                      handlers.onDelete(task);
                    }}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function Row({
  task,
  extras,
  handlers,
  closing,
  sortable,
  swipeEnabled,
  quietClicks,
  hold,
  onDelete,
}: {
  task: Task;
  extras: TaskExtras;
  handlers: TaskHandlers;
  closing: ClosingHold;
  sortable: boolean;
  swipeEnabled: boolean;
  quietClicks: () => void;
  hold: 'holding' | 'armed' | undefined;
  onDelete: () => void;
}): React.ReactElement {
  const { listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    disabled: !sortable,
  });

  const targets = swipeTargets(task, extras.shownSprint, shownStatus(task, closing) === 'done');
  const swipe = useSwipe({
    targets,
    enabled: swipeEnabled,
    onCommit: (side) => {
      const target = targets[side];
      if (target?.kind === 'move') handlers.onMove(task, target.sprint);
      if (target?.kind === 'trash') handlers.onDelete(task);
    },
    onGesture: quietClicks,
  });
  const { ref: swipeRef, view } = swipe;
  const ref = useCallback(
    (element: HTMLElement | null) => {
      setNodeRef(element);
      swipeRef(element);
    },
    [setNodeRef, swipeRef],
  );

  // Пока строку тянут вбок, её положение задаёт палец, а не сортировка.
  const style: React.CSSProperties =
    view === undefined
      ? { transform: CSS.Translate.toString(transform), transition }
      : {
          transform: `translate3d(${String(view.offset)}px, 0, 0)`,
          transition: view.phase === 'move' ? 'none' : `transform ${String(SETTLE_MS)}ms ease-out`,
        };

  return (
    <>
      {view !== undefined && (
        <SwipeUnder view={view} targets={targets} current={extras.currentSprint} />
      )}
      {/*
        Строку берут целиком, как карточку доски: ручка на каждой строке съела
        бы место у названия, а на телефоне его и так мало (3.1.7). Роль и
        атрибуты `@dnd-kit` строке не отдаются — по той же причине, что у
        карточки: кнопки внутри кнопки быть не может. Сменить статус без
        перетаскивания по-прежнему можно кнопками (3.1.4).
      */}
      <li
        ref={ref}
        className={`row row--${shownStatus(task, closing)}${
          closing.isClosing(task) ? ' row--closing' : ''
        }${isDragging ? ' is-dragging' : ''}`}
        style={style}
        {...pointerListeners(listeners)}
        onClick={(event) => openOnBlankClick(event, () => handlers.onOpen(task))}
      >
        <span className="row__body">
          {/*
          Заголовок — кнопка: карточка открывается поверх списка и не
          уводит со спринта (3.5.1).
        */}
          <span className="row__head">
            <button type="button" className="row__title" onClick={() => handlers.onOpen(task)}>
              {task.title}
            </button>
            {/*
            Действия справа: правый край телефона — то, куда палец
            дотягивается сам. Отметка «готово» стоит последней, у
            самого края: её нажимают чаще всего.
          */}
            {hold === 'armed' ? (
              // На месте действий, а не поверх строки: палец только что
              // отпустил её, и кнопка там, куда он и так смотрит, — у края.
              <button type="button" className="row__delete" onClick={onDelete}>
                Удалить
              </button>
            ) : (
              <>
                <RowAction task={task} extras={extras} handlers={handlers} />
                <DoneToggle
                  task={task}
                  done={closing.isDone(task)}
                  onToggle={() => closing.toggle(task)}
                />
              </>
            )}
          </span>
          <TaskMarks task={task} extras={extras} handlers={handlers} className="row__marks" />
        </span>
        {closing.isClosing(task) && <ClosingTimer />}
        {hold === 'holding' && <DeleteHoldTimer ms={DELETE_HOLD_MS - LONG_PRESS_MS} />}
      </li>
    </>
  );
}

/**
 * Подложка под строкой, которую тянут вбок: куда задача уедет. Порог пройден
 * — подложка темнеет: на iPhone отклик на пороге может не прозвучать, и цвет
 * остаётся главным знаком «дотянул».
 *
 * Лежит рядом со строкой, а не внутри: внутри её закрыл бы фон самой строки.
 * От озвучки скрыта — это картинка жеста, а не элемент управления; тот же
 * перенос доступен в карточке задачи.
 */
function SwipeUnder({
  view,
  targets,
  current,
}: {
  view: SwipeView;
  targets: SwipeTargets;
  current: SprintKey;
}): React.ReactElement {
  const target = targets[view.side];
  return (
    <li
      aria-hidden="true"
      className={`swipe swipe--${view.side}${target === undefined ? ' swipe--none' : ''}${
        target?.kind === 'trash' ? ' swipe--trash' : ''
      }${view.armed || view.phase === 'gone' ? ' is-armed' : ''}`}
      style={{ top: view.top, height: view.height }}
    >
      {target !== undefined && (
        <span className="swipe__label">{swipeLabel(target, view.side, current)}</span>
      )}
    </li>
  );
}

function swipeLabel(target: SwipeTarget, side: 'left' | 'right', current: SprintKey): string {
  const where =
    target.kind === 'trash'
      ? 'В удалённые'
      : target.sprint === null
        ? 'Во Входящие'
        : relativeSprintTitle(target.sprint, current);
  return side === 'right' ? `→ ${where}` : `${where} ←`;
}
